import {
	FieldArray,
	Form,
	withServerErrors,
	MarkdownInputField,
	DateRangePickerField,
	SelectInputField,
	validateFormValues,
	GetFieldValue,
	SubmissionError,
	arrayMutators,
	SwitchInputField,
	SelectField,
} from "@sembark-travel/ui-form"
import { IMealPlan, SelectMealPlans } from "../MealPlans"
import { IRoomType } from "../RoomTypes"
import {
	Box,
	Button,
	Col,
	Divider,
	Grid,
	Icons,
	Inline,
	Select,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import * as Validator from "yup"
import { THotelNote } from "./store"
import { useXHR } from "@sembark-travel/xhr"
import { formatDate } from "@sembark-travel/datetime-utils"
import { SelectTripDestination, TTripDestination } from "../TripDestinations"

type IFormHotelNotes = {
	notes?: Array<{
		show: string
		show_on_hotel: 1 | 0
		room_types?: IRoomType[]
		meal_plans?: IMealPlan[]
		dates: Array<{
			start_date?: string
			end_date?: string
		}>
		description?: string
		all_dates: 1 | 0
		all_meals: 1 | 0
		all_rooms: 1 | 0
		destinations?: Array<TTripDestination>
	}>
}

const hotelNotesValidation = validateFormValues(
	Validator.object().shape({
		notes: Validator.array()
			.min(1, "Please add at least one note")
			.of(
				Validator.object().shape({
					description: Validator.string()
						.required("Please add a description for the note")
						.trim()
						.max(1000, "Please keep your note within 1000 characters."),

					dates: Validator.array().of(
						Validator.object().shape({
							end_date: Validator.date()
								.nullable()
								.when("start_date", ([start_date], schema) => {
									if (start_date) {
										return schema.min(
											start_date,
											"End date must be after start date"
										)
									}
									return schema
								}),
						})
					),
				})
			),
	})
)

const hotelNotesInitialValues: Required<IFormHotelNotes> = {
	notes: [
		{
			show: "quote_and_voucher",
			show_on_hotel: 1,
			room_types: [],
			meal_plans: [],
			dates: [
				{
					start_date: undefined,
					end_date: undefined,
				},
			],
			description: "",
			all_dates: 0,
			all_meals: 0,
			all_rooms: 0,
		},
	],
}
export function UpdatedHotelNotes({
	mealPlans,
	roomTypes,
	note,
	isGlobalNotes,
	refresh,
}: {
	mealPlans?: IMealPlan[]
	note: THotelNote
	refresh: () => void
	roomTypes?: IRoomType[]
	isGlobalNotes: boolean
}) {
	const xhr = useXHR()
	const {
		room_types,
		meal_plans,
		description,
		date_ranges,
		show_in_voucher,
		show_in_quote,
		destinations,
	} = note
	const show_on_hotel =
		(room_types?.length || 0) === 0 &&
		(meal_plans?.length || 0) === 0 &&
		(date_ranges?.length || 0) === 0
			? 1
			: 0
	const initialValues: IFormHotelNotes = {
		notes: [
			{
				show:
					show_in_voucher && show_in_quote
						? "quote_and_voucher"
						: show_in_voucher
							? "voucher"
							: show_in_quote
								? "quote"
								: "",
				show_on_hotel: show_on_hotel,
				room_types: room_types,
				meal_plans: meal_plans,
				dates:
					Array.isArray(date_ranges) && date_ranges.length > 0
						? date_ranges.map(({ start_date, end_date }) => ({
								start_date: start_date || undefined,
								end_date: end_date || undefined,
							}))
						: [
								{
									start_date: undefined,
									end_date: undefined,
								},
							],
				description,
				all_dates: (date_ranges?.length || 0) === 0 && !show_on_hotel ? 1 : 0,
				all_meals: (meal_plans?.length || 0) === 0 && !show_on_hotel ? 1 : 0,
				all_rooms: (room_types?.length || 0) === 0 && !show_on_hotel ? 1 : 0,
				destinations: destinations ?? [],
			},
		],
	}
	return (
		<HotelNotesForm
			roomTypes={roomTypes}
			mealPlans={mealPlans}
			initialValues={initialValues}
			onSubmit={async (values) => {
				const data = transformNotesToRequestData(values)
				await xhr.patch(`/hotel-notes/${note.id}`, {
					...data[0],
				})
				refresh()
			}}
			isEditing={true}
			isGlobalNotes={isGlobalNotes}
		/>
	)
}
function transformNotesToRequestData(values: IFormHotelNotes) {
	return (values.notes || []).map(
		({
			show,
			meal_plans,
			room_types,
			dates,
			description,
			show_on_hotel,
			destinations,
		}) => {
			const showQuote = show === "quote_and_voucher" || show === "quote" ? 1 : 0
			const showVoucher =
				show === "quote_and_voucher" || show === "voucher" ? 1 : 0
			if (show_on_hotel === 0) {
				return {
					description,
					show_in_quote: showQuote,
					show_in_voucher: showVoucher,
					meal_plans: meal_plans?.map((mp) => mp.id),
					room_types: room_types?.map((rt) => rt.id),
					date_ranges: dates.map(({ start_date, end_date }) => {
						if (!start_date && !end_date) return {}

						const formatted: Record<string, string> = {}
						if (start_date)
							formatted.start_date = formatDate(start_date, "YYYY-MM-DD")
						if (end_date)
							formatted.end_date = formatDate(end_date, "YYYY-MM-DD")

						return formatted
					}),
					destinations:
						destinations?.map((destination) => destination.id) || [],
				}
			} else {
				return {
					description,
					show_in_quote: showQuote,
					show_in_voucher: showVoucher,
					destinations:
						destinations?.map((destination) => destination.id) || [],
				}
			}
		}
	)
}

/**
 *  @hotelId and @roomType are optional in case of global notes
 */
export function CreateHotelNotes({
	mealPlans,
	hotelId,
	roomTypes,
	onSuccess,
	isGlobalNotes,
}: {
	mealPlans?: IMealPlan[]
	hotelId?: number
	roomTypes?: IRoomType[]
	onSuccess?: () => void
	isGlobalNotes: boolean
}) {
	const xhr = useXHR()
	return (
		<HotelNotesForm
			roomTypes={roomTypes}
			mealPlans={mealPlans}
			initialValues={hotelNotesInitialValues}
			onSubmit={async (values) => {
				const data = transformNotesToRequestData(values)
				await xhr.post(`/hotel-notes`, { notes: data, hotel_id: hotelId })
				onSuccess?.()
			}}
			isGlobalNotes={isGlobalNotes}
		/>
	)
}

export function HotelNotesForm({
	roomTypes,
	mealPlans,
	initialValues,
	isEditing = false,
	isGlobalNotes = false,
	onSubmit,
	onCancel,
}: {
	initialValues: IFormHotelNotes
	mealPlans?: IMealPlan[]
	roomTypes?: IRoomType[]
	isEditing?: boolean
	isGlobalNotes?: boolean
	onSubmit: (data: IFormHotelNotes) => Promise<void>
	onCancel?: () => void
}) {
	return (
		<Form<IFormHotelNotes>
			initialValues={initialValues}
			validate={hotelNotesValidation}
			onSubmit={withServerErrors(async (val) => {
				await onSubmit(val)
			})}
			mutators={{ ...arrayMutators }}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, form, submitting }) => (
				<form noValidate onSubmit={handleSubmit}>
					<FieldArray<Required<IFormHotelNotes>["notes"][number]> name="notes">
						{({ fields }) => (
							<>
								<Stack gap={"4"}>
									{fields.map((name, index) => (
										<Box
											key={index}
											paddingY={"2"}
											borderColor={"muted"}
											rounded="md"
											borderWidth={"1"}
										>
											<Grid>
												<Col lg={6} md={12} sm={12} xs={12}>
													<Stack gap={"2"}>
														<Grid>
															<Col paddingX={"0"}>
																<SelectInputField
																	name={`${name}.show`}
																	label="Share With"
																	required
																	maxWidth={"xs"}
																>
																	<option value="quote_and_voucher">
																		Quotation and Voucher
																	</option>
																	<option value="voucher">Voucher only</option>
																	<option value="quote">Quotation only</option>
																	<option value="">Internal use only</option>
																</SelectInputField>
															</Col>
															<Col>
																<Inline
																	gap="2"
																	alignItems={"center"}
																	paddingTop={"6"}
																>
																	<SwitchInputField
																		label={
																			isGlobalNotes
																				? "General (Applicable on all hotel combinations)"
																				: "Applicable on complete Hotel"
																		}
																		name={`${name}.show_on_hotel`}
																		value="1"
																		onChange={(value) => {
																			form.batch(() => {
																				if (value) {
																					form.batch(() => {
																						form.change(
																							`${name}.show_on_hotel` as never,
																							1 as never
																						)
																						form.change(
																							`${name}.room_types` as never,
																							[] as never
																						)
																						form.change(
																							`${name}.meal_plans` as never,
																							[] as never
																						)
																						form.change(
																							`${name}.dates` as never,
																							[
																								{
																									start_date: undefined,
																									end_date: undefined,
																								},
																							] as never
																						)
																						form.change(
																							`${name}.all_rooms` as never,
																							0 as never
																						)
																						form.change(
																							`${name}.all_meals` as never,
																							0 as never
																						)
																						form.change(
																							`${name}.all_dates` as never,
																							0 as never
																						)
																					})
																				} else {
																					form.change(
																						`${name}.show_on_hotel` as never,
																						0 as never
																					)
																				}
																			})
																		}}
																	/>
																</Inline>
															</Col>
														</Grid>

														<GetFieldValue<1 | 0>
															name={`${name}.show_on_hotel`}
														>
															{({ value: complete_hotel }) => (
																<Stack gap={"2"}>
																	{roomTypes ? (
																		<Grid>
																			<Col paddingX={"0"}>
																				<Box maxWidth={"xs"}>
																					<GetFieldValue<1 | 0>
																						name={`${name}.all_rooms`}
																					>
																						{({ value: all_rooms }) => (
																							<SelectField
																								select={Select}
																								name={`${name}.room_types`}
																								label="Room Type"
																								type="room_type"
																								multiple
																								options={roomTypes}
																								secondaryLabel={"optional"}
																								disabled={
																									complete_hotel === 1 ||
																									all_rooms === 1
																								}
																							/>
																						)}
																					</GetFieldValue>
																				</Box>
																			</Col>
																			<Col>
																				<Inline
																					gap="2"
																					alignItems={"center"}
																					paddingTop={"6"}
																				>
																					<SwitchInputField
																						label={"For all rooms"}
																						name={`${name}.all_rooms`}
																						value="1"
																						disabled={complete_hotel === 1}
																						onChange={(value) => {
																							if (value) {
																								form.change(
																									`${name}.all_rooms` as never,
																									1 as never
																								)
																								form.change(
																									`${name}.room_types` as never,
																									[] as never
																								)
																							} else {
																								form.change(
																									`${name}.all_rooms` as never,
																									0 as never
																								)
																							}
																						}}
																					/>
																				</Inline>
																			</Col>
																		</Grid>
																	) : null}

																	<Grid>
																		<Col paddingX={"0"}>
																			<Box maxWidth={"xs"}>
																				<GetFieldValue<1 | 0>
																					name={`${name}.all_meals`}
																				>
																					{({ value: all_meals }) =>
																						!mealPlans && isGlobalNotes ? (
																							<SelectField
																								select={SelectMealPlans}
																								name={`${name}.meal_plans`}
																								label="Meal Plan"
																								multiple
																								secondaryLabel={"optional"}
																								disabled={
																									complete_hotel === 1 ||
																									all_meals === 1
																								}
																							/>
																						) : (
																							<SelectField
																								select={Select}
																								name={`${name}.meal_plans`}
																								label="Meal Plan"
																								multiple
																								options={mealPlans}
																								secondaryLabel={"optional"}
																								disabled={
																									complete_hotel === 1 ||
																									all_meals === 1
																								}
																							/>
																						)
																					}
																				</GetFieldValue>
																			</Box>
																		</Col>

																		<Col>
																			<Inline
																				gap="2"
																				alignItems={"center"}
																				paddingTop={"6"}
																			>
																				<SwitchInputField
																					label={"For all meals"}
																					name={`${name}.all_meals`}
																					value="1"
																					disabled={complete_hotel === 1}
																					onChange={(value) => {
																						if (value) {
																							form.change(
																								`${name}.all_meals` as never,
																								1 as never
																							)
																							form.change(
																								`${name}.meal_plans` as never,
																								[] as never
																							)
																						} else {
																							form.change(
																								`${name}.all_meals` as never,
																								0 as never
																							)
																						}
																					}}
																				/>
																			</Inline>
																		</Col>
																	</Grid>

																	<Box>
																		<Grid>
																			<Col paddingX={"0"}>
																				<Stack gap={"2"}>
																					<FieldArray<
																						Required<IFormHotelNotes>["notes"][number]["dates"][number]
																					>
																						name={`${name}.dates`}
																					>
																						{({ fields }) =>
																							fields.map((dates, index) => (
																								<Inline
																									gap="2"
																									alignItems={"start"}
																									key={index}
																								>
																									<GetFieldValue<0 | 1>
																										name={`${name}.all_dates`}
																									>
																										{({
																											value: for_all_dates,
																										}) => (
																											<Stack gap={"2"}>
																												<Inline
																													gap="4"
																													alignItems={"start"}
																												>
																													<Box maxWidth={"xs"}>
																														<DateRangePickerField
																															label="Range Between"
																															fromName={`${dates}.start_date`}
																															toName={`${dates}.end_date`}
																															dateFormat="DD MMM 'YY"
																															secondaryLabel={
																																"optional"
																															}
																															rangePresets={
																																null
																															}
																															disabled={
																																complete_hotel ===
																																	1 ||
																																for_all_dates ===
																																	1
																															}
																														/>
																													</Box>

																													{index > 0 ? (
																														<Inline
																															paddingTop={"8"}
																															cursor="pointer"
																															onClick={() =>
																																fields.remove(
																																	index
																																)
																															}
																														>
																															<Icons.Cancel />
																														</Inline>
																													) : null}
																												</Inline>
																												{(fields.length || 1) -
																													1 ===
																												index ? (
																													<Box>
																														<Button
																															onClick={() =>
																																fields.push({})
																															}
																															disabled={
																																complete_hotel ===
																																	1 ||
																																for_all_dates ===
																																	1
																															}
																														>
																															<Icons.Plus />
																															Add date
																														</Button>
																													</Box>
																												) : null}
																											</Stack>
																										)}
																									</GetFieldValue>
																								</Inline>
																							))
																						}
																					</FieldArray>
																				</Stack>
																			</Col>
																			<Col>
																				<Inline
																					gap="2"
																					alignItems={"center"}
																					paddingTop={"6"}
																				>
																					<SwitchInputField
																						label={"For all days"}
																						name={`${name}.all_dates`}
																						value="1"
																						disabled={complete_hotel === 1}
																						onChange={(value) => {
																							if (value) {
																								form.change(
																									`${name}.all_dates` as never,
																									1 as never
																								)
																								form.change(
																									`${name}.dates` as never,
																									[
																										{
																											start_date: undefined,
																											end_date: undefined,
																										},
																									] as never
																								)
																							} else {
																								form.change(
																									`${name}.all_dates` as never,
																									0 as never
																								)
																							}
																						}}
																					/>
																				</Inline>
																			</Col>
																		</Grid>
																	</Box>
																</Stack>
															)}
														</GetFieldValue>
														{isGlobalNotes ? (
															<Box maxWidth={"sm"} width={"full"}>
																<GetFieldValue<TTripDestination[]>
																	name={`${name}.destinations`}
																>
																	{({ value }) => (
																		<Box
																			display={"flex"}
																			flexDirection={"col"}
																			gap={"1"}
																		>
																			<SelectField
																				name={`${name}.destinations`}
																				label="Destinations"
																				select={SelectTripDestination}
																				multiple
																				fetchOnMount={
																					!initialValues.notes?.[index]
																						?.destinations?.length
																				}
																				closeOnSelect
																				secondaryLabel={"optional"}
																			/>
																			{value.length ? null : (
																				<Text fontSize={"xs"}>
																					* This note will be applicable to all
																					destinations if no destination is
																					selected.
																				</Text>
																			)}
																		</Box>
																	)}
																</GetFieldValue>
															</Box>
														) : null}
													</Stack>
												</Col>
												<Col>
													<Stack gap={"1"}>
														<MarkdownInputField
															label="Notes"
															name={`${name}.description`}
															placeholder="Enter notes here..."
															required
															rows={7}
														/>
														{index > 0 ? (
															<Inline justifyContent={"end"}>
																<Button
																	level="tertiary"
																	onClick={() => fields.remove(index)}
																>
																	<Icons.Cancel /> Remove
																</Button>
															</Inline>
														) : null}
													</Stack>
												</Col>
											</Grid>
										</Box>
									))}
								</Stack>
								{!isEditing ? (
									<Box paddingTop={"4"}>
										<Button
											type="button"
											onClick={() =>
												fields.push(hotelNotesInitialValues.notes[0])
											}
										>
											<Icons.Plus />
											Add More
										</Button>
									</Box>
								) : null}
							</>
						)}
					</FieldArray>

					<SubmissionError />
					<Divider sm />
					<Inline gap={"2"}>
						<Button type="submit" disabled={submitting}>
							{submitting ? "Saving ..." : "Save"}
						</Button>
						{onCancel ? (
							<Button
								type="button"
								onClick={onCancel}
								level="secondary"
								disabled={submitting}
							>
								Cancel
							</Button>
						) : null}
					</Inline>
				</form>
			)}
		</Form>
	)
}
